import React, { useMemo } from 'react';

import type { BlockInfoCard } from 'types/block-info-card';

import { Wrap, Card, Button, Paragraph } from '@leafwell/components';

import Figure from './figure';
import WysiwygContent from './wysiwyg-content';
import HeadingBody from './heading-body';
import ArrowButton from './arrow-button';

export type InfoContentType = Pick<
  BlockInfoCard,
  'arrowButton' | 'tag' | 'tagIcon' | 'description'
> & {
  direction?: 'horizontal' | 'vertical';
  className?: string;
};

export type TagContentType = Pick<InfoContentType, 'tag' | 'tagIcon'> & {
  className?: string;
};

export type DescriptionContentType = Pick<
  InfoContentType,
  'description' | 'arrowButton'
> & { className?: string };

const DescriptionContent: React.FC<DescriptionContentType> = ({
  description,
  arrowButton,
  className,
}) => {
  return (
    <span className={['grid gap-y-4', className].join(' ')}>
      <HeadingBody
        {...{
          level: '2',
          tagName: 'p',
          title: description,
        }}
      />
      <ArrowButton
        title={arrowButton?.title}
        href={arrowButton?.url}
        filled="purple"
        className="justify-center	w-full lg:w-fit lg:h-fit"
        classNameImage="rotate-90"
      />
    </span>
  );
};

const TagContent: React.FC<TagContentType> = ({ tag, tagIcon, className }) => {
  return (
    <span className={['flex items-center gap-x-3', className].join(' ')}>
      {tagIcon && <Figure {...tagIcon} className="max-w-6" />}
      {tag && <Paragraph>{tag}</Paragraph>}
    </span>
  );
};

const InfoContent: React.FC<InfoContentType> = ({
  direction,
  tag,
  tagIcon,
  description,
  arrowButton,
  className,
}) => {
  const isVertical = direction === 'vertical';
  return isVertical ? (
    <div className={['order-first', className].join(' ')}>
      <TagContent {...{ tag, tagIcon }} />
      <DescriptionContent
        {...{ description, arrowButton }}
        className="mt-4 lg:mt-8"
      />
    </div>
  ) : (
    <>
      <TagContent {...{ tag, tagIcon }} className="self-start	" />
      <DescriptionContent
        {...{ description, arrowButton }}
        className="col-span-full lg:col-span-2 -mt-4 lg:mt-auto"
      />
    </>
  );
};

const InfoCard: React.FC<BlockInfoCard> = ({
  sectionId,
  layout,
  tag,
  tagIcon,
  description,
  descriptionAlignment,
  image,
  imageAlignment,
  arrowButton,
  cards,
}) => {
  // primary layout means that the description and tag are above the card
  // secondary layout means that the description and tag are side by side with the card
  const isPrimary = layout === 'primary';

  const classNames = useMemo(() => {
    return {
      wrap: [
        'gap-y-8 lg:gap-y-12',
        description === null ? 'lg:gap-x-12' : 'lg:gap-x-28',
        isPrimary ? 'lg:grid-cols-3' : 'lg:grid-cols-2',
      ].join(' '),

      imagePrimary: ['rounded-md col-span-2'].join(' '),

      imageSecondary: [
        'rounded-md',
        imageAlignment === 'left' ? 'order-first' : 'order-last',
      ].join(' '),

      cards: [
        'flex',
        isPrimary
          ? image !== null
            ? 'hidden lg:block lg:col-start-3 h-fit'
            : 'lg:col-start-2 lg:col-span-2 gap-x-4'
          : '',
      ].join(' '),
    };
  }, [layout, description, cards, image]);

  return (
    <Wrap id={sectionId} className={classNames.wrap}>
      {(description || tag) && (
        <InfoContent
          {...{
            tag,
            tagIcon,
            description,
            arrowButton,
          }}
          direction={isPrimary ? 'horizontal' : 'vertical'}
          className={
            descriptionAlignment === 'left' ? 'lg:order-first' : 'lg:order-last'
          }
        />
      )}
      {image && !isPrimary && (
        <Figure {...image} className={classNames.imageSecondary} />
      )}
      <span
        className={
          isPrimary ? 'lg:col-span-full lg:grid lg:grid-cols-3 gap-x-4' : ''
        }
      >
        {image && isPrimary && (
          <Figure {...image} className={classNames.imagePrimary} />
        )}
        {cards?.length > 0 && (
          <div className={classNames.cards}>
            {cards.map(({ content, image, button }, idx) => {
              const onlyImage = content === null;
              const isFirst = idx === 0;
              return (
                <Card
                  key={idx}
                  filled
                  rounded
                  variant="medium"
                  className={[
                    !isFirst ? 'hidden lg:block' : '',
                    onlyImage ? 'p-0' : 'p-8 md:p-12',
                  ].join(' ')}
                >
                  <div
                    className={
                      image && !onlyImage ? 'grid grid-cols-2 gap-x-8' : ''
                    }
                  >
                    {content && <WysiwygContent content={content} />}
                    {image && (
                      <Figure
                        {...image}
                        className="max-h-72 md:max-h-152 lg:max-h-fit order-first rounded-2xl"
                      />
                    )}
                  </div>
                  {button && (
                    <Button
                      {...{
                        href: button.url,
                        label: button.title,
                        rounded: true,
                        target: button.target,
                        variant: 'secondary',
                        className: 'self-center w-fit h-fit',
                      }}
                    />
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </span>
    </Wrap>
  );
};

export default InfoCard;
